import { CameraControls } from '@react-three/drei';
import { Object3D, Vector3 } from 'three';

import {
  DEFAULT_CAMERA_POSITION,
  DEFAULT_THUMBNAIL_POSITION,
} from './components/SceneSetup';

// We need to keep in mind that our system handles frames with "index zero"
// But the user should see "index one"
// The following functions seem trivial but they will help developers understand better what happens
// For sanity you can remove `+ 1` and `- 1` and see the actual numbers in the UI
export function formatFrames(value: number) {
  return value + 1;
}
export function parseSliderValue(value: number) {
  return value - 1;
}

/**
 * Follows the target with the camera controls
 * @param delta - The delta time
 * @param target - The target to follow
 * @param cameraControls - The camera controls to use
 * @param serializedCamera - The serialized camera
 * @param isThumbnail - Whether the camera is a thumbnail
 */
export default function cameraControlsFollow({
  delta,
  target,
  cameraControls,
  serializedCamera,
  isThumbnail,
}: {
  delta: number;
  target?: Object3D;
  cameraControls?: CameraControls | null;
  serializedCamera?: string;
  isThumbnail: boolean;
}) {
  if (!target || !cameraControls) return;

  const defaultPosition = isThumbnail
    ? DEFAULT_THUMBNAIL_POSITION
    : DEFAULT_CAMERA_POSITION;

  // Calculate offset:
  let offset = new Vector3(...defaultPosition).sub(
    new Vector3(...target.position),
  );
  if (serializedCamera) {
    const { positionX, positionY, positionZ, targetX, targetY, targetZ } =
      JSON.parse(serializedCamera);

    offset = new Vector3(positionX, positionY, positionZ).sub(
      new Vector3(targetX, targetY, targetZ),
    );
  }

  //
  // Camera follow logic:

  // Get position of character
  const targetPosition = new Vector3();
  target.getWorldPosition(targetPosition);

  // Correct for initial camera position
  // TODO: Figure out how to sync initial camera position and target
  targetPosition.y -= 0.5;

  // ensure that for thumbnails the character is positioned more centrally
  if (isThumbnail) {
    targetPosition.y += 0.4;
  }

  // Calculate the desired camera position
  const desiredPosition = targetPosition.clone().add(offset);
  // Make the camera look at the target
  cameraControls.setLookAt(
    desiredPosition.x,
    desiredPosition.y,
    desiredPosition.z,
    targetPosition.x,
    targetPosition.y,
    targetPosition.z,
    false,
  );
  cameraControls.update(delta);
}
